import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/state/store/auth';
import { setRememberMePreference } from '@/state/store/auth/auth-storage';
import { hasSessionCookie } from '@/modules/auth/services/auth.service';
import { completeOidcLogin } from '@/modules/auth/services/oidc.service';

/**
 * Completes the hosted (OIDC) login on the callback URL: IAM's `idp/callback` turns `code`/`state`
 * into the session, either as tokens in the body or as HttpOnly cookies only.
 */
export function useOidcActivation() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, logout, setOidcSession } = useAuthStore();
  const effectRan = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice; a code can be exchanged only once.
    if (effectRan.current) return;
    effectRan.current = true;

    const fail = () => {
      logout();
      navigate('/login', {
        replace: true,
        state: { ssoError: t('VERIFICATION_FAILED_PLEASE_TRY_AGAIN') },
      });
    };

    async function activate() {
      try {
        const res = await completeOidcLogin(window.location.href);
        const accessToken = res?.access_token ?? res?.accessToken;

        if (accessToken) {
          // SSO has no remember-me UI; keep persistent sessions (previous default).
          setRememberMePreference(true);
          login(accessToken, res.refresh_token ?? res.refreshToken ?? '');
          setOidcSession(true);
          return navigate('/hr/directory', { replace: true });
        }

        if (!res?.error && (await hasSessionCookie())) {
          setRememberMePreference(true);
          login('', '');
          setOidcSession(true);
          return navigate('/hr/directory', { replace: true });
        }

        console.error('[OIDC] Callback failed:', res?.error, res?.error_description);
        fail();
      } catch (error) {
        console.error('[OIDC] Callback error:', error);
        fail();
      }
    }

    activate();
  }, [login, logout, navigate, setOidcSession, t]);
}
